import React from 'react';
import {Divider, Stack, Typography} from '@mui/material';
import {Drawer} from 'presentation/components/Drawer';
import {HandleMessage} from 'presentation/components/HandleMessage';
import {IconIllustration} from 'presentation/components';
import {FeaturesEnum, RefundEnum} from 'presentation/constants';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {translations} from 'presentation/translations-new';
import {useNavigate} from 'react-router-dom';
import protocolIndicator from 'presentation/assets/images/protocol-number-indicator.webp';
import protocolIndicatorRefund from 'presentation/assets/images/protocol-number-indicator-refund.webp';
import type {RefundInfoDrawerProps} from './types';

const {
	title,
	description,
	altText,
	secondDescription,
	primaryButton,
	secondaryButton,
	imageDescription,
	titleWithProtocol,
	descriptionWithProtocolPrefix,
	descriptionWithProtocolBoldRefund,
	descriptionWithProtocolMiddle,
	descriptionWithProtocolBold,
	refundImageDescription,
	protocolLocationPrefix,
	protocolLocationBoldProtocol,
	protocolLocationMiddle,
	protocolLocationBold,
	protocolImageDescription,
} = translations['pt-br'].privateFlow.refund.additionalDocuments.searchProtocol.refundInfoDrawer;

export function RefundInfoDrawer({open, onClose}: RefundInfoDrawerProps) {
	const navigate = useNavigate();
	const {isEnabled: isViewSupplementaryDocumentsEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_REFUND_VIEW_SUPPLEMENTARY_DOCUMENTS,
	);

	const handleNavigate = () => {
		navigate(RefundEnum.CONSULT);
	};

	return (
		<Drawer.Root
			customMaxHeight="100%"
			width="420px"
			onOpen={() => {}}
			open={open}
			onClose={onClose}>
			<Drawer.Header containerPadding="0px 16px 16px" onClose={onClose} />

			<Drawer.Content sx={{gap: 2}}>
				<Stack alignItems="center">
					<IconIllustration size="small" variant="info" />
				</Stack>

				{isViewSupplementaryDocumentsEnabled ? (
					<>
						<Typography variant="h6">{titleWithProtocol}</Typography>
						<Divider />
						<Typography variant="body1">
							{descriptionWithProtocolPrefix}
							<Typography component="span" fontWeight={600}>
								{descriptionWithProtocolBoldRefund}
							</Typography>
							{descriptionWithProtocolMiddle}
							<Typography component="span" fontWeight={600}>
								{descriptionWithProtocolBold}
							</Typography>
						</Typography>
						<Stack gap="4px">
							<Stack
								sx={{
									border: 1,
									borderColor: 'divider',
									borderRadius: 1,
									overflow: 'hidden',
									alignSelf: 'flex-start',
									width: '100%',
								}}>
								<img src={protocolIndicator} alt={altText} />
							</Stack>
							<Typography variant="helperText" fontWeight={400}>
								{refundImageDescription}
							</Typography>
						</Stack>
						<Divider />
						<Typography variant="body1">
							{protocolLocationPrefix}
							<Typography component="span" fontWeight={600}>
								{protocolLocationBoldProtocol}
							</Typography>
							{protocolLocationMiddle}
							<Typography component="span" fontWeight={600}>
								{protocolLocationBold}
							</Typography>
						</Typography>
						<Stack gap="4px">
							<Stack
								sx={{
									border: 1,
									borderColor: 'divider',
									borderRadius: 1,
									overflow: 'hidden',
									alignSelf: 'flex-start',
									width: '100%',
								}}>
								<img src={protocolIndicatorRefund} alt={altText} />
							</Stack>
							<Typography variant="helperText" fontWeight={400}>
								{protocolImageDescription}
							</Typography>
						</Stack>
						<Typography variant="body1">{secondDescription}</Typography>
					</>
				) : (
					<>
						<Typography variant="h6">{title}</Typography>
						<Divider />
						<Typography variant="body1">
							<HandleMessage message={description} />
						</Typography>
						<Stack gap="4px">
							<Stack
								sx={{
									border: 1,
									borderColor: 'divider',
									borderRadius: 1,
									overflow: 'hidden',
									alignSelf: 'flex-start',
								}}>
								<img src={protocolIndicator} alt={altText} />
							</Stack>
							<Typography variant="helperText" fontWeight={400}>
								{imageDescription}
							</Typography>
						</Stack>
						<Typography variant="body1">{secondDescription}</Typography>
					</>
				)}
			</Drawer.Content>

			<Drawer.Footer
				direction="column-reverse"
				firstButtonTitle={primaryButton}
				secondButtonOnClick={onClose}
				firstButtonOnClick={handleNavigate}
				secondButtonTitle={secondaryButton}
			/>
		</Drawer.Root>
	);
}
